<template>
  <div class="container text-secondary">
    <div class="row mb-3">
      <div class="col-2"/>
      <div class="col">
        <div class="h4">Bokkerijen</div>
      </div>
      <div class="col-2 text-end pe-2">
        <img src="@/assets/back-48.png" alt="terug" class="img-fluid"
             style="height:32px; cursor:pointer;" @click="$router.push('/')"/>
      </div>
    </div>

    <hr class="divider">

    <!-- Setup -->
    <div v-if="!gameStarted">
      <div class="card-modern">
        <div class="row mb-2">
          <div class="col text-end">Kaartdek:</div>
          <div class="col text-start">
            <button v-for="size in [32, 52]" :key="size"
                    class="btn btn-sm me-2"
                    :class="cardDeck === size ? 'btn-primary' : 'btn-outline-secondary'"
                    @click="cardDeck = size">
              {{ size }}
            </button>
          </div>
        </div>
        <div class="row">
          <div class="col text-end">Aantal rondes:</div>
          <div class="col text-start">{{ numberOfRounds }}</div>
        </div>
      </div>
      <div class="row">
        <div class="col text-center">
          <button class="btn-primary-modern" @click="startGame">Begin</button>
        </div>
      </div>
    </div>

    <!-- Spel bezig -->
    <div v-else>
      <div v-if="currentRound <= rounds.length" class="card-modern mb-3">

        <!-- Deler indicator -->
        <div class="dealer-bar mb-2">
          <span>🃏 Deler: <strong>{{ players[dealerIndex].name }}</strong></span>
          <span style="color:#a0aec0; font-size:0.85rem;">
            🎯 Aan beurt: {{ players[currentPlayer].name }}
          </span>
        </div>

        <div class="row mb-2">
          <div class="col text-center">
            <strong>Ronde {{ rounds[currentRound - 1].round }}</strong>
            &nbsp;·&nbsp;
            {{ rounds[currentRound - 1].numberOfCards }}
            {{ rounds[currentRound - 1].numberOfCards === 1 ? 'kaart' : 'kaarten' }}
            &nbsp;·&nbsp;
            {{ rounds[currentRound - 1].trump ? 'met troef' : 'zonder troef' }}
          </div>
        </div>

        <!-- Troef kiezen -->
        <div v-if="rounds[currentRound - 1].trump" class="row mb-3">
          <div class="col-3 text-center">
            <img src="@/assets/heart-64.png" alt="harten" class="img-fluid troef-img"
                 :class="currentTrump === 'hearts' || currentTrump === '' ? '' : 'gray'"
                 @click="currentTrump = 'hearts'"/>
          </div>
          <div class="col-3 text-center">
            <img src="@/assets/clubs-64.png" alt="klaveren" class="img-fluid troef-img"
                 :class="currentTrump === 'clubs' || currentTrump === '' ? '' : 'gray'"
                 @click="currentTrump = 'clubs'"/>
          </div>
          <div class="col-3 text-center">
            <img src="@/assets/diamond-64.png" alt="ruiten" class="img-fluid troef-img"
                 :class="currentTrump === 'diamonds' || currentTrump === '' ? '' : 'gray'"
                 @click="currentTrump = 'diamonds'"/>
          </div>
          <div class="col-3 text-center">
            <img src="@/assets/spades-64.png" alt="schoppen" class="img-fluid troef-img"
                 :class="currentTrump === 'spades' || currentTrump === '' ? '' : 'gray'"
                 @click="currentTrump = 'spades'"/>
          </div>
        </div>

        <div v-if="!scoringStarted" class="row">
          <div class="col text-center">
            <button class="btn-primary-modern" @click="startScoring"
                    :disabled="rounds[currentRound - 1].trump && !currentTrump">
              Scores invoeren
            </button>
          </div>
        </div>

        <!-- Score invoer -->
        <div v-if="scoringStarted">
          <div class="row mb-2">
            <div class="col text-center">
              Score van <strong>{{ players[currentPlayer].name }}</strong>
            </div>
          </div>
          <div class="row mb-2">
            <div class="col-3 text-center" v-if="rounds[currentRound-1].numberOfCards >= 0">
              <img src="@/assets/numbers/circled-0-64.png" alt="0" class="img-fluid score-img" @click="enterScore(0)"/>
            </div>
            <div class="col-3 text-center" v-if="rounds[currentRound-1].numberOfCards >= 1">
              <img src="@/assets/numbers/circled-1-64.png" alt="1" class="img-fluid score-img" @click="enterScore(1)"/>
            </div>
            <div class="col-3 text-center" v-if="rounds[currentRound-1].numberOfCards >= 2">
              <img src="@/assets/numbers/circled-2-64.png" alt="2" class="img-fluid score-img" @click="enterScore(2)"/>
            </div>
            <div class="col-3 text-center" v-if="rounds[currentRound-1].numberOfCards >= 3">
              <img src="@/assets/numbers/circled-3-64.png" alt="3" class="img-fluid score-img" @click="enterScore(3)"/>
            </div>
          </div>
          <div class="row mb-2">
            <div class="col-3 text-center" v-if="rounds[currentRound-1].numberOfCards >= 4">
              <img src="@/assets/numbers/circled-4-64.png" alt="4" class="img-fluid score-img" @click="enterScore(4)"/>
            </div>
            <div class="col-3 text-center" v-if="rounds[currentRound-1].numberOfCards >= 5">
              <img src="@/assets/numbers/circled-5-64.png" alt="5" class="img-fluid score-img" @click="enterScore(5)"/>
            </div>
            <div class="col-3 text-center" v-if="rounds[currentRound-1].numberOfCards >= 6">
              <img src="@/assets/numbers/circled-6-64.png" alt="6" class="img-fluid score-img" @click="enterScore(6)"/>
            </div>
            <div class="col-3 text-center" v-if="rounds[currentRound-1].numberOfCards >= 7">
              <img src="@/assets/numbers/circled-7-64.png" alt="7" class="img-fluid score-img" @click="enterScore(7)"/>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="card-modern text-center">
        <div style="font-size:2rem;">🏆</div>
        <div class="h5 mt-2">Spel klaar!</div>
      </div>

      <!-- Scorebord -->
      <div class="card-modern">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <div class="h6 mb-0">Scorebord</div>
          <button class="edit-toggle" @click="editModus = !editModus">
            <img src="@/assets/edit-48.png" alt="bewerken" style="height:22px;"/>
            {{ editModus ? 'Klaar' : 'Wijzig' }}
          </button>
        </div>

        <div v-if="editModus" style="font-size:0.8rem; color:#a0aec0; margin-bottom:8px;">
          Tik op een score om te wijzigen
        </div>

        <div class="scorebord-wrapper">
          <table class="sticky-table">
            <thead>
            <tr>
              <th class="col-naam sticky-links">Speler</th>
              <th v-for="n in allRoundsWithScores" :key="n" class="col-ronde">
                R{{ rounds[n-1].round }}
              </th>
              <th class="col-totaal sticky-rechts">Tot</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(player, pi) in players" :key="player.id">
              <td class="col-naam sticky-links">{{ player.name }}</td>
              <td v-for="n in allRoundsWithScores" :key="n"
                  class="col-ronde"
                  :class="{ 'edit-cel': editModus }"
                  @click="editModus ? openEdit(pi, n-1) : null">
                {{ rounds[n-1].scores[pi] ?? '' }}
              </td>
              <td class="col-totaal sticky-rechts">{{ playerTotal(pi) }}</td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Edit popup -->
    <div v-if="editPopup" class="popup-overlay" @click.self="editPopup = false">
      <div class="popup-box">
        <div style="font-weight:700; color:#4a5568; margin-bottom:12px; text-align:center;">
          Score wijzigen: <strong>{{ players[editSpeler].name }}</strong> · R{{ rounds[editRonde].round }}
        </div>
        <div class="score-grid">
          <button v-for="n in rounds[editRonde].numberOfCards + 1" :key="n-1"
                  class="score-num-btn" @click="slaEditOp(n-1)">
            {{ n-1 }}
          </button>
        </div>
        <button class="btn-secondary-modern mt-3" style="width:100%;" @click="editPopup = false">
          Annuleren
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import store from "@/store";

export default {
  name: "BokkerijenGame",
  data() {
    return {
      cardDeck: 52,
      gameStarted: false,
      scoringStarted: false,
      currentRound: 1,
      currentTrump: '',
      currentPlayer: 0,
      rounds: [],
      dealerIndex: 0,
      rondeScores: {},
      editModus: false,
      editPopup: false,
      editSpeler: null,
      editRonde: null,
    };
  },
  computed: {
    players() {
      return store.state.playersRoom.length >= 2
          ? store.state.playersRoom
          : store.state.players;
    },
    numberOfRounds() {
      return Math.floor(this.cardDeck / this.players.length) * 2 + 2;
    },
    allRoundsWithScores() {
      return this.rounds
          .map((r, i) => i + 1)
          .filter(n => Object.keys(this.rounds[n-1].scores).length > 0);
    },
  },
  methods: {
    startGame() {
      const normal = Math.floor(this.cardDeck / this.players.length);
      this.rounds = [];
      let rn = 1;
      for (let i = 1; i <= normal; i++) {
        this.rounds.push({ round: rn++, numberOfCards: i, trump: true, scores: {} });
      }
      this.rounds.push({ round: rn++, numberOfCards: normal, trump: false, scores: {} });
      this.rounds.push({ round: rn++, numberOfCards: normal, trump: false, scores: {} });
      for (let i = normal; i >= 1; i--) {
        this.rounds.push({ round: rn++, numberOfCards: i, trump: true, scores: {} });
      }
      this.currentRound = 1;
      this.dealerIndex = 0;
      this.currentPlayer = (this.dealerIndex + 1) % this.players.length;
      this.gameStarted = true;
    },
    startScoring() {
      this.currentPlayer = (this.dealerIndex + 1) % this.players.length;
      this.scoringStarted = true;
    },
    enterScore(score) {
      // Sla op met spelerindex als key
      this.rounds[this.currentRound - 1].scores[this.currentPlayer] = score;
      const gespeeld = Object.keys(this.rounds[this.currentRound - 1].scores).length;

      if (gespeeld < this.players.length) {
        this.currentPlayer = (this.dealerIndex + 1 + gespeeld) % this.players.length;
      } else {
        this.scoringStarted = false;
        this.currentTrump = '';
        this.dealerIndex = (this.dealerIndex + 1) % this.players.length;
        this.currentPlayer = (this.dealerIndex + 1) % this.players.length;
        this.currentRound++;
      }
    },
    playerTotal(pi) {
      return this.rounds.reduce((sum, r) => sum + (r.scores[pi] ?? 0), 0);
    },
    openEdit(pi, rondeIndex) {
      this.editSpeler = pi;
      this.editRonde = rondeIndex;
      this.editPopup = true;
    },
    slaEditOp(score) {
      this.rounds[this.editRonde].scores[this.editSpeler] = score;
      this.editPopup = false;
    },
  },
}
</script>

<style scoped>
.gray { filter: grayscale(1); opacity: 50%; }
.troef-img { cursor: pointer; max-height: 48px; }
.score-img { cursor: pointer; max-height: 56px; }

.dealer-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f0f4ff;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 0.9rem;
  color: #4a5568;
}

.scorebord-wrapper {
  overflow-x: auto;
  border-radius: 8px;
}

.sticky-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.9rem;
}

.sticky-table th,
.sticky-table td {
  padding: 6px 8px;
  text-align: center;
  border-bottom: 1px solid #f0f4f8;
  white-space: nowrap;
  background: white;
}

.sticky-table thead th {
  color: #718096;
  font-weight: 700;
  border-bottom: 2px solid #e2e8f0;
}

.sticky-table tr:last-child td {
  border-bottom: none;
}

.col-naam {
  text-align: left !important;
  font-weight: 600;
  color: #2d3748;
  min-width: 80px;
}

.sticky-links {
  position: sticky;
  left: 0;
  z-index: 2;
  box-shadow: 2px 0 4px rgba(0,0,0,0.06);
}

.col-totaal {
  font-weight: 800 !important;
  color: #667eea !important;
  min-width: 44px;
}

.sticky-rechts {
  position: sticky;
  right: 0;
  z-index: 2;
  box-shadow: -2px 0 4px rgba(0,0,0,0.06);
}

.col-ronde {
  min-width: 36px;
}

.edit-toggle {
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  padding: 4px 10px;
  font-size: 0.85rem;
  cursor: pointer;
  color: #4a5568;
  transition: all 0.15s;
}
.edit-toggle:hover { border-color: #667eea; color: #667eea; }
.edit-cel { cursor: pointer; }
.edit-cel:hover { background: #ebf4ff !important; border-radius: 4px; }

.score-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}
.score-num-btn {
  background: #f7fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
  font-size: 1.2rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
}
.score-num-btn:hover { border-color: #667eea; background: #ebf4ff; color: #667eea; }
.btn-secondary-modern {
  background: #e2e8f0;
  color: #4a5568;
  border: none;
  border-radius: 12px;
  padding: 10px 24px;
  font-size: 1rem;
  cursor: pointer;
}
.popup-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.popup-box {
  background: white;
  border-radius: 16px;
  padding: 20px;
  width: 90%;
  max-width: 360px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.2);
}
</style>